import React, { memo, useEffect, useState } from 'react';
import type { CatalogItem } from '@lumos/contracts';
import { posterSrcSet } from './images.js';
import { lastInput, watchInput, type InputVia } from './input-modality.js';

// Следим за вводом с первой карточки: нажатие Tab до первого фокуса уже учтено.
watchInput();

/**
 * Карточка каталога — общая для главной, разделов, истории, избранного и
 * фильмографии человека.
 *
 * Живёт отдельным модулем, а не в `main.tsx`, по одной приземлённой причине:
 * `main.tsx` при импорте монтирует всё приложение (`createRoot`), и страница,
 * которой нужна только карточка, тянула за собой запуск приложения целиком.
 * В тестах это падало сразу («Target container is not a DOM element»), а в
 * сборке означало лишний круг зависимостей между страницами и точкой входа.
 */

const KIND_LABEL: Record<CatalogItem['kind'], string> = {
  movie: 'Фильм',
  tv: 'Сериал',
};

/** Что можно сделать с карточкой в истории. Без него меню не показывается. */
export interface CardActions {
  onRemove: () => void;
  onReset: () => void;
  onWatched: () => void;
}

/** Сколько держать нажатие на постере, чтобы открылось меню. */
const LONG_PRESS_MS = 500;

/**
 * Какую ширину карточка занимает на экране — подсказка браузеру, какой из
 * вариантов `srcset` брать. Совпадает с `flex-basis` карточек в styles.css.
 */
const POSTER_SIZES = '(max-width: 768px) 132px, clamp(150px, 11.5vw, 220px)';

/** Карточка каталога — клик ведёт на отдельную страницу контента. */
function CardView({
  item,
  progressPercent,
  actions,
  wide = false,
  caption,
  onFocusItem,
  onLeaveItem,
}: {
  item: CatalogItem;
  progressPercent?: number;
  actions?: CardActions;
  /**
   * Горизонтальный кадр 16:9 вместо постера — у «Продолжить просмотр»: там
   * узнают сцену, на которой остановились, а не афишу.
   */
  wide?: boolean;
  /** Подпись под названием вместо «год · тип» («Осталось 52 мин»). */
  caption?: string;
  /**
   * Карточка в фокусе или под курсором — главная показывает её тайтл наверху,
   * списки ставят её кадр подложкой. `via` — чем до неё дошли: клавиатурой
   * (фокус) или указателем (курсор, палец; фокус от щелчка — тоже указатель).
   */
  onFocusItem?: (item: CatalogItem, via: InputVia) => void;
  /** Фокус или курсор ушли с карточки — главная возвращается к перелистыванию. */
  onLeaveItem?: () => void;
}): React.JSX.Element {
  const go = (): void => {
    window.location.hash = `#/title/${encodeURIComponent(item.id)}`;
  };
  // Полоса — только у реально начатого: следующая серия на полке «Продолжить»
  // приходит с нулём, а случайно открытая на секунду — с долями процента, и
  // пустая полоса на такой карточке читалась как «уже смотрел».
  const progress = progressPercent != null && progressPercent >= 1 ? Math.min(100, progressPercent) : null;

  // Картинка проявляется, только когда она декодирована: иначе карточка
  // показывала бы её построчную прорисовку поверх заглушки.
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const backdrop = item.backdrop?.url ?? null;
  const poster = item.poster?.url ?? null;
  // У широкой карточки без кадра афиша заполняет рамку целиком, верхней
  // частью — там лица и название. Раньше она стояла узкой полосой по центру
  // поверх своей размытой копии, и карточка выглядела сломанной (замечание
  // владельца 09.10.2026). Сервер к тому же подставляет сериалу кадр серии,
  // так что без кадра остаются единицы.
  const posterInWide = wide && backdrop === null && poster !== null;
  const src = failed ? null : wide ? (backdrop ?? poster) : poster;

  // Долгое нажатие: на телефоне это единственный жест «дай меню», на мыши тот
  // же смысл несёт правая кнопка — поддерживаем оба, чтобы не заставлять
  // держать палец там, где привычнее контекстное меню.
  const [menu, setMenu] = useState(false);
  const pressTimer = React.useRef<number | null>(null);
  const longFired = React.useRef(false);

  const cancelPress = (): void => {
    if (pressTimer.current !== null) window.clearTimeout(pressTimer.current);
    pressTimer.current = null;
  };
  const startPress = (): void => {
    if (!actions) return;
    longFired.current = false;
    cancelPress();
    pressTimer.current = window.setTimeout(() => {
      longFired.current = true;
      setMenu(true);
    }, LONG_PRESS_MS);
  };

  useEffect(() => {
    if (!menu) return;
    // Закрываем по любому клику мимо и по Esc: меню поверх карточек не должно
    // требовать попадания в крестик.
    const close = (): void => setMenu(false);
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setMenu(false);
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  useEffect(() => cancelPress, []);

  const run = (action: () => void) => (event: React.MouseEvent): void => {
    event.stopPropagation();
    setMenu(false);
    action();
  };

  const focusItem = onFocusItem ? (): void => onFocusItem(item, lastInput()) : undefined;
  const pointItem = onFocusItem ? (): void => onFocusItem(item, 'pointer') : undefined;

  const card = (
    <button
      type="button"
      className={wide ? 'card card--wide' : 'card'}
      onClick={() => {
        // Клик после долгого нажатия — это не переход, а «отпустил палец».
        if (longFired.current) {
          longFired.current = false;
          return;
        }
        go();
      }}
      onFocus={focusItem}
      onBlur={onLeaveItem}
      onPointerEnter={pointItem}
      onPointerDown={startPress}
      onPointerUp={cancelPress}
      onPointerLeave={() => {
        cancelPress();
        onLeaveItem?.();
      }}
      onContextMenu={(event) => {
        if (!actions) return;
        event.preventDefault();
        setMenu(true);
      }}
      aria-label={`Открыть страницу: ${item.title}`}
    >
      <div
        className={['poster', loaded || src === null ? 'is-loaded' : '', progress != null ? 'has-progress' : '']
          .filter(Boolean)
          .join(' ')}
      >
        <span className="poster-media">
          {src !== null ? (
            <img
              className={posterInWide ? 'is-poster-fill' : undefined}
              src={src}
              srcSet={wide ? undefined : posterSrcSet(src)}
              sizes={wide ? undefined : POSTER_SIZES}
              alt=""
              loading="lazy"
              decoding="async"
              ref={(node) => {
                // Из кэша картинка бывает готова раньше, чем React повесит
                // обработчик, — тогда `load` уже не придёт.
                if (node?.complete && node.naturalWidth > 0) setLoaded(true);
              }}
              onLoad={() => setLoaded(true)}
              onError={() => setFailed(true)}
            />
          ) : (
            <span className="poster-initial" aria-hidden="true">
              {item.title.slice(0, 1)}
            </span>
          )}
        </span>
        {!wide && item.ratings.average != null && (
          <span className="card-badge">★ {item.ratings.average.toFixed(1)}</span>
        )}
        {progress != null && (
          <span className="card-progress" aria-hidden="true">
            <span className="card-progress-fill" style={{ transform: `scaleX(${progress / 100})` }} />
          </span>
        )}
        <span className="card-ring" aria-hidden="true" />
      </div>
      <h3>{item.title}</h3>
      <small>
        {caption ?? (
          <>
            {item.releaseYear ?? ''}
            {item.releaseYear ? ' · ' : ''}
            {KIND_LABEL[item.kind]}
          </>
        )}
      </small>
    </button>
  );

  if (!actions) return card;
  return (
    <div className={wide ? 'card-holder card-holder--wide' : 'card-holder'} onPointerDown={(event) => event.stopPropagation()}>
      {card}
      {menu && (
        <div className="card-menu" role="menu" aria-label={`Действия: ${item.title}`}>
          <button type="button" role="menuitem" onClick={run(actions.onWatched)}>
            Просмотрено
          </button>
          <button type="button" role="menuitem" onClick={run(actions.onReset)}>
            Сбросить прогресс
          </button>
          <button type="button" role="menuitem" className="danger" onClick={run(actions.onRemove)}>
            Удалить
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Карточек на главной — сотни, а перерисовывать их при каждом движении фокуса
 * незачем: свойства у карточки меняются редко, и `memo` пропускает её целиком.
 */
export const Card = memo(CardView);
